/**
 * Wo die Kundendokumente liegen.
 *
 * Vier Wege stehen zur Wahl. Der Grund für die Auswahl ist nicht Geschmack:
 * ein Server mit fester Platte wächst mit jedem Jahr, und irgendwann ist sie
 * voll. Liegen die Unterlagen dort, wo das Büro sie ohnehin hat – SharePoint,
 * Nextcloud oder ein Storage-Server –, wächst der Speicher getrennt und
 * kostet nur, was er belegt.
 *
 * Der Knopf „Verbindung prüfen" schreibt eine Probedatei, liest sie zurück
 * und löscht sie wieder. Eine Ablage, an der man sich nur anmelden kann, ist
 * noch keine Ablage: Schreiben und Lesen sind zwei verschiedene
 * Berechtigungen, und fiele der Unterschied erst beim ersten echten Dokument
 * auf, stünde der Prüfer damit auf dem Platz.
 */

import { useEffect, useState } from 'react'
import {
  ABLAGEARTTEXT,
  bytesLesbar,
  useAblage,
  useAblageProbe,
  useAblageSetzen,
  type Ablageart,
} from '../api/kundenakte'
import { Fehlerhinweis, Ladeanzeige } from '../bausteine/Hinweise'

const ARTEN = Object.keys(ABLAGEARTTEXT) as Ablageart[]

/** Was zu jeder Ablageart erklärt werden muss, bevor jemand sie wählt. */
const ERKLAERUNG: Record<Ablageart, string> = {
  lokal:
    'Alles liegt auf dem Server. Ohne Einrichtung sofort nutzbar – der Speicher ' +
    'wächst aber mit jedem Dokument, und die Platte ist endlich.',
  sharepoint:
    'Nutzt denselben Zugang wie der Mailversand. In Entra braucht die Anwendung ' +
    'die Berechtigung Sites.Selected und zusätzlich die Freigabe genau dieser ' +
    'einen Website – die zweite wird gern vergessen.',
  webdav:
    'Für Nextcloud ein App-Passwort verwenden, nicht das Anmeldekennwort: es ' +
    'lässt sich einzeln zurückziehen und umgeht die Zwei-Faktor-Anmeldung nicht.',
  s3:
    'Endpunkt, Eimer und Zugangsschlüssel des Anbieters. Für einen Speicher ' +
    'außerhalb von AWS ist der Endpunkt zwingend.',
}

export function Ablagebereich() {
  const stand = useAblage()
  const setzen = useAblageSetzen()
  const probe = useAblageProbe()
  const [art, setzeArt] = useState<Ablageart>('lokal')
  const [felder, setzeFelder] = useState<Record<string, string>>({})

  // Die geladenen Werte einmal übernehmen. Kennwörter kommen nie zurück –
  // ihre Felder bleiben leer und bedeuten "unverändert lassen".
  useEffect(() => {
    if (!stand.data) return
    setzeArt(stand.data.art)
    setzeFelder({
      webdav_url: stand.data.webdav_url ?? '',
      webdav_benutzer: stand.data.webdav_benutzer ?? '',
      webdav_passwort: '',
      s3_endpunkt: stand.data.s3_endpunkt ?? '',
      s3_region: stand.data.s3_region ?? '',
      s3_eimer: stand.data.s3_eimer ?? '',
      s3_schluessel_id: stand.data.s3_schluessel_id ?? '',
      s3_geheimnis: '',
      sp_host: stand.data.sp_host ?? '',
      sp_pfad: stand.data.sp_pfad ?? '',
      sp_ordner: stand.data.sp_ordner ?? 'SPUER',
    })
  }, [stand.data])

  if (stand.isLoading) return <Ladeanzeige />
  if (stand.error) return <Fehlerhinweis fehler={stand.error} />

  function setze(name: string, wert: string) {
    setzeFelder((alt) => ({ ...alt, [name]: wert }))
  }

  function speichern(ereignis: React.FormEvent) {
    ereignis.preventDefault()
    probe.reset()
    setzen.mutate({ art, ...felder })
  }

  const daten = stand.data!

  return (
    <div className="space-y-4">
      <div className="karte-akzent">
        <p>
          <span className="font-semibold">{daten.dokumente_gesamt}</span>{' '}
          {daten.dokumente_gesamt === 1 ? 'Dokument' : 'Dokumente'} mit zusammen{' '}
          <span className="font-semibold">{bytesLesbar(daten.bytes_gesamt)}</span>.
        </p>
        <p className="mt-1 text-base text-slate-700">
          Ein Wechsel der Ablage gilt nur für neue Dokumente. Was schon
          hochgeladen wurde, bleibt dort liegen, wo es liegt, und wird von dort
          weiter geholt – es geht nichts verloren.
        </p>
      </div>

      <form onSubmit={speichern} className="karte space-y-4">
        <div>
          <label htmlFor="ablageart">Ablage</label>
          <select
            id="ablageart"
            value={art}
            onChange={(e) => setzeArt(e.target.value as Ablageart)}
          >
            {ARTEN.map((a) => (
              <option key={a} value={a}>
                {ABLAGEARTTEXT[a]}
              </option>
            ))}
          </select>
          <p className="mt-2 text-base text-slate-700">{ERKLAERUNG[art]}</p>
        </div>

        {art === 'sharepoint' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Feld
              name="sp_host"
              beschriftung="Server"
              platzhalter="contoso.sharepoint.com"
              wert={felder.sp_host}
              setzen={setze}
            />
            <Feld
              name="sp_pfad"
              beschriftung="Website"
              platzhalter="/sites/Pruefbuero"
              wert={felder.sp_pfad}
              setzen={setze}
            />
            <Feld
              name="sp_ordner"
              beschriftung="Ordner in der Bibliothek"
              platzhalter="SPUER"
              wert={felder.sp_ordner}
              setzen={setze}
            />
          </div>
        )}

        {art === 'webdav' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Feld
                name="webdav_url"
                beschriftung="Adresse"
                platzhalter="https://wolke.example.de/remote.php/dav/files/bernd/SPUER"
                wert={felder.webdav_url}
                setzen={setze}
              />
            </div>
            <Feld
              name="webdav_benutzer"
              beschriftung="Benutzer"
              wert={felder.webdav_benutzer}
              setzen={setze}
            />
            <Geheimfeld
              name="webdav_passwort"
              beschriftung="App-Passwort"
              gesetzt={daten.webdav_passwort_gesetzt}
              wert={felder.webdav_passwort}
              setzen={setze}
            />
          </div>
        )}

        {art === 's3' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Feld
              name="s3_endpunkt"
              beschriftung="Endpunkt"
              platzhalter="https://s3.eu-central-1.example.com"
              wert={felder.s3_endpunkt}
              setzen={setze}
            />
            <Feld
              name="s3_region"
              beschriftung="Region"
              platzhalter="eu-central-1"
              wert={felder.s3_region}
              setzen={setze}
            />
            <Feld
              name="s3_eimer"
              beschriftung="Eimer (Bucket)"
              platzhalter="spuer-dokumente"
              wert={felder.s3_eimer}
              setzen={setze}
            />
            <Feld
              name="s3_schluessel_id"
              beschriftung="Zugriffsschlüssel"
              wert={felder.s3_schluessel_id}
              setzen={setze}
            />
            <Geheimfeld
              name="s3_geheimnis"
              beschriftung="Geheimer Schlüssel"
              gesetzt={daten.s3_geheimnis_gesetzt}
              wert={felder.s3_geheimnis}
              setzen={setze}
            />
          </div>
        )}

        <Fehlerhinweis fehler={setzen.error} />

        <div className="flex flex-wrap gap-2">
          <button type="submit" className="knopf-haupt" disabled={setzen.isPending}>
            {setzen.isPending ? 'Speichert …' : 'Speichern'}
          </button>
          <button
            type="button"
            className="knopf-zweit"
            onClick={() => probe.mutate()}
            disabled={probe.isPending}
          >
            {probe.isPending ? 'Prüft …' : 'Verbindung prüfen'}
          </button>
        </div>

        {probe.data && (
          <p
            className={`rounded-feld border-2 p-3 ${
              probe.data.erfolgreich
                ? 'border-stufe-ob bg-stufe-ob-grund text-stufe-ob'
                : 'border-stufe-3 bg-stufe-3-grund text-stufe-3'
            }`}
          >
            <span className="font-semibold">
              {probe.data.erfolgreich ? 'Die Ablage antwortet. ' : 'Es hat nicht geklappt: '}
            </span>
            {probe.data.meldung}
          </p>
        )}
      </form>
    </div>
  )
}

function Feld({
  name,
  beschriftung,
  platzhalter,
  wert,
  setzen,
}: {
  name: string
  beschriftung: string
  platzhalter?: string
  wert?: string
  setzen: (name: string, wert: string) => void
}) {
  return (
    <div>
      <label htmlFor={name}>{beschriftung}</label>
      <input
        id={name}
        value={wert ?? ''}
        placeholder={platzhalter}
        onChange={(e) => setzen(name, e.target.value)}
        autoComplete="off"
      />
    </div>
  )
}

/**
 * Kennwortfeld.
 *
 * Der Wert kommt nie vom Server zurück. Leer heißt deshalb „unverändert",
 * nicht „löschen" – sonst wäre jede Änderung an der Adresse zugleich ein
 * Löschen des Kennworts.
 */
function Geheimfeld({
  name,
  beschriftung,
  gesetzt,
  wert,
  setzen,
}: {
  name: string
  beschriftung: string
  gesetzt: boolean
  wert?: string
  setzen: (name: string, wert: string) => void
}) {
  return (
    <div>
      <label htmlFor={name}>{beschriftung}</label>
      <input
        id={name}
        type="password"
        value={wert ?? ''}
        placeholder={gesetzt ? '•••••••• (bleibt, wenn leer)' : 'noch nicht gesetzt'}
        onChange={(e) => setzen(name, e.target.value)}
        autoComplete="new-password"
      />
      <p className="mt-1 text-base text-slate-600">
        {gesetzt
          ? 'Ist hinterlegt und wird nie wieder angezeigt. Leer lassen heißt: unverändert.'
          : 'Wird verschlüsselt gespeichert.'}
      </p>
    </div>
  )
}
